import React, { useMemo, useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { X, CheckCircle2, XCircle, Users, Building2, UsersRound, Boxes, Search, ChevronDown } from "lucide-react";
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { CommanServices, normalizeIncidentRecipientDirectory } from '@/services/commanServices';
import ConditionalFollowupTrigger from './ConditionalFollowupTrigger';
import axios from 'axios';
import { ApiEndPoint } from '@/services/apiUttiLities';
import { isWebAdmin } from '@/auth/webAccess';
import { DutyBadge } from './PersonnelOfferPrompt';
import RecipientPicker, { resolveRecipients as resolveSharedRecipients } from './RecipientPicker';
let _raDutyPromise = null;
import ScenarioRoleField, { RoleGuidanceBanner, blankRole, buildScenarioRoleOverridePatch, canOverrideScenarioRoles, cleanRoles, markScenarioRolesForOverride, rolesValid, rolesSummary } from './ScenarioRoleField';
import { shouldUseGeoFenceRecipientCreate, timelineWriteSucceeded } from './geoFenceActions';


// Helper to parse timestamps correctly (handles both timezone-aware and naive timestamps)
const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  // If timestamp has timezone info (+00:00, Z), use as-is
  if (timestamp.includes('+') || timestamp.includes('Z')) {
    return new Date(timestamp);
  }
  // If no timezone, assume UTC and append 'Z'
  return new Date(timestamp + 'Z');
};

const computeTasksWithState = (tasks, notifiedAtStr, currentTime) => {
  const sortedTasks = [...tasks].sort((a, b) => (a.order || 0) - (b.order || 0));
  let currentStart = notifiedAtStr ? parseTimestamp(notifiedAtStr) : new Date();
  if (!currentStart) currentStart = new Date();
  
  let foundActive = false;
  
  return sortedTasks.map((task) => {
    const durationMs = (Number(task.timerMinutes) || 5) * 60 * 1000;
    const deadline = new Date(currentStart.getTime() + durationMs);
    
    let state = 'queued'; // 'completed', 'active', 'failed', 'queued'
    let remainingMs = deadline.getTime() - currentTime.getTime();
    
    if (task.isCompleted) {
      state = 'completed';
      currentStart = task.completedAt ? parseTimestamp(task.completedAt) : deadline;
      if (!currentStart) currentStart = deadline;
    } else {
      if (currentTime.getTime() > deadline.getTime()) {
        state = 'failed';
        currentStart = deadline;
      } else {
        if (!foundActive) {
          state = 'active';
          foundActive = true;
        } else {
          state = 'queued';
        }
        currentStart = deadline;
      }
    }
    
    return {
      ...task,
      state,
      deadline,
      remainingMs,
    };
  });
};

const ResponseActionModal = ({ incident, editingItem, onClose, onSuccess, prefilledName, prefilledUserIds = [], prefilledSelections = null, geoFenceTargeting = null, isImmediateTask = false, conditionalContext = null, onAddConditional = null, scenarioStaffingDefaults = null, onRoleCatalogUpsert = null, onlineUserIds = null, presenceByUserId = {}, allowedRecipientUserIds = null, allowedRecipientDepartmentIds = null, allowedRecipientTeamIds = null, allowedRecipientCrewIds = null }) => {
  const recipientScopeRestricted = [
    allowedRecipientUserIds,
    allowedRecipientDepartmentIds,
    allowedRecipientTeamIds,
    allowedRecipientCrewIds,
  ].some(Array.isArray);
  const [formData, setFormData] = useState({
    performedAt: null, // Will be set when Mark as Performed button is clicked
    performanceStatus: 'Not Performed',
    tasks: [], // Array of {taskId, taskText, isCompleted, completedBy, completedAt, notes}
    responseTime: '', // No default SLA — empty/0 means "no SLA entered"
  });

  const [performedByUser, setPerformedByUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPerformed, setIsPerformed] = useState(false); // Track if Performed button was clicked
  const [performButtonLoading, setPerformButtonLoading] = useState(false);
  const [appUsers, setAppUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [crewOptions, setCrewOptions] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  const [targetTab, setTargetTab] = useState('users');
  const [dutyMap, setDutyMap] = useState({});
  useEffect(() => {
    if (recipientScopeRestricted && incident?.id) {
      CommanServices.getIncidentRecipientDirectory(incident.id)
        .then(normalizeIncidentRecipientDirectory)
        .then((directory) => setDutyMap(directory.duty))
        .catch(() => setDutyMap({}));
      return;
    }
    if (!_raDutyPromise) {
      _raDutyPromise = axios
        .get(`${ApiEndPoint}/api/duty-status`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } })
        .then((r) => r.data?.data?.users || {})
        .catch(() => ({}));
    }
    _raDutyPromise.then(setDutyMap);
  }, [incident?.id, recipientScopeRestricted]);
  const [userSearch, setUserSearch] = useState('');
  const [actionCardName, setActionCardName] = useState('');
  const [escalation, setEscalation] = useState(null); // Phase 2: conditional inject rule
  const [newTaskText, setNewTaskText] = useState(''); // For adding new tasks
  const [newTaskTimer, setNewTaskTimer] = useState(5); // Default timer for new tasks (in minutes)
  const [currentTime, setCurrentTime] = useState(new Date());
  const [roles, setRoles] = useState([blankRole()]);
  const [note, setNote] = useState('');
  const [roleOverrideMeta, setRoleOverrideMeta] = useState({});
  const [savedActionCards, setSavedActionCards] = useState([]);
  // Searchable "Use a saved Action Card" combobox
  const [savedCardQuery, setSavedCardQuery] = useState('');
  const [savedCardOpen, setSavedCardOpen] = useState(false);
  const [availableResources, setAvailableResources] = useState([]); // Resource injects defined in this scenario
  const [linkPickerTask, setLinkPickerTask] = useState(null); // taskId whose resource picker is open — or '__card__' for the whole action card
  const [pickResourceId, setPickResourceId] = useState('');
  const [pickQty, setPickQty] = useState(1);
  const [resourceUseBusy, setResourceUseBusy] = useState('');
  // Resources linked to the WHOLE action card (persisted at meta.linkedResources)
  const [cardLinkedResources, setCardLinkedResources] = useState([]);
  useEffect(() => {
    setCardLinkedResources(Array.isArray(editingItem?.meta?.linkedResources) ? editingItem.meta.linkedResources : []);
  }, [editingItem?.id]);

  const isActiveExercise = String(incident?.status || '').toLowerCase() === 'active';

  // Load resources (group 'resource') so tasks and the card can link to them.
  // Also computes how much of each resource is ALREADY linked on other cards/
  // tasks, so availability shown here is live and strict.
  const loadAvailableResources = async () => {
    if (!incident?.id) return;
    try {
      const [res, usageRes] = await Promise.all([
        CommanServices.getincidentTimeline(incident.id),
        CommanServices.getIncidentResourceUsage(incident.id),
      ]);
      const data = res?.status ? (await res.data) : (res?.data ?? res ?? []);
      const items = Array.isArray(data) ? data : (data?.items || []);
      const usageItems = usageRes?.status && Array.isArray(usageRes.data) ? usageRes.data : [];
      const usageById = Object.fromEntries(usageItems.map((resource) => [String(resource.id), resource]));
      const linkedElsewhere = {}; // resourceId -> qty linked outside this modal's card
      items.forEach((it) => {
        if (it.is_active === false || it.id === editingItem?.id) return;
        (it?.meta?.linkedResources || []).forEach((lr) => {
          if (lr?.resourceId) linkedElsewhere[lr.resourceId] = (linkedElsewhere[lr.resourceId] || 0) + (Number(lr.quantity) || 0);
        });
        (it?.meta?.tasks || []).forEach((t) => (t?.linkedResources || []).forEach((lr) => {
          if (lr?.resourceId) linkedElsewhere[lr.resourceId] = (linkedElsewhere[lr.resourceId] || 0) + (Number(lr.quantity) || 0);
        }));
      });
      setAvailableResources(
        items
          .filter((it) => it.group === 'resource' && it.is_active !== false)
          .map((it) => ({
            id: it.id,
            category: usageById[it.id]?.category || it.meta?.resource?.category || it.title || 'Resource',
            quantity: Number(usageById[it.id]?.quantity ?? it.meta?.resource?.quantity) || 0,
            consumed: Number(usageById[it.id]?.usageSummary?.consumed ?? it.meta?.resource?.consumed) || 0,
            availability: usageById[it.id]?.availability || it.meta?.resource?.availability || 'once',
            cost: Number(usageById[it.id]?.cost ?? it.meta?.resource?.cost) || 0,
            replenishEvery: usageById[it.id]?.replenishEvery || it.meta?.resource?.replenishEvery || '',
            replenishUnit: usageById[it.id]?.replenishUnit || it.meta?.resource?.replenishUnit || 'min',
            usageSummary: usageById[it.id]?.usageSummary || it.meta?.resource?.usageSummary || {},
            usageEvents: usageById[it.id]?.usageEvents || it.meta?.resource?.usageEvents || [],
            linkedElsewhere: linkedElsewhere[it.id] || 0,
          }))
      );
    } catch (e) {
      setAvailableResources([]);
    }
  };

  // Units of a resource linked inside THIS modal (card-level + every task).
  const linkedInModal = (resourceId) => {
    let n = 0;
    cardLinkedResources.forEach((lr) => { if (lr.resourceId === resourceId) n += Number(lr.quantity) || 0; });
    (formData.tasks || []).forEach((t) => (t.linkedResources || []).forEach((lr) => {
      if (lr.resourceId === resourceId) n += Number(lr.quantity) || 0;
    }));
    return n;
  };
  // Every resource type has a finite incident stock. The linked quantity is a
  // strict use cap for single-use/time-based resources and an initial
  // allocation for reusable resources, which may draw extra available stock.
  const liveAvail = (r) => Math.max(
    0,
    (Number(r.quantity) || 0) - (Number(r.linkedElsewhere) || 0) - linkedInModal(r.id)
  );
  const availLabel = (r) => (
    `${r.category} · ${AVAILABILITY_SHORT[r.availability] || r.availability} · ${liveAvail(r)}/${r.quantity} available`
  );
  const AVAILABILITY_SHORT = { once: 'single-use', reuse: 'reusable', time: 'time-based' };
  const resourceForLink = (link) => {
    const current = availableResources.find((resource) => String(resource.id) === String(link?.resourceId));
    if (!current) return link;
    return {
      ...current,
      category: link?.category || current.category,
      availability: link?.availability || current.availability,
      cost: link?.cost ?? current.cost,
      replenishEvery: link?.replenishEvery ?? current.replenishEvery,
      replenishUnit: link?.replenishUnit || current.replenishUnit,
      note: link?.note ?? current.note,
    };
  };
  const contextUsageEvents = (resource, taskId) => (resource?.usageEvents || []).filter((event) => (
    String(event?.actionCardId || '') === String(editingItem?.id || '') &&
    String(event?.taskId || '') === String(taskId || '') &&
    !event?.reversedAt &&
    String(event?.status || '').toLowerCase() !== 'reversed'
  ));
  const formatResourceMoney = (value) => `SAR ${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const replenishLabel = (resource) => {
    const amount = Number(resource?.replenishEvery) || 0;
    const unit = resource?.replenishUnit === 'hour' ? 'hr' : resource?.replenishUnit === 'day' ? 'day' : 'min';
    return amount > 0 ? `♻ ${amount} ${unit}` : '♻';
  };

  const handleResourceUsage = async (taskId, link, operation, eventId = null) => {
    if (!editingItem?.id || !incident?.id) return;
    const key = `${taskId || 'card'}:${link.resourceId}:${operation}`;
    if (resourceUseBusy) return;
    setResourceUseBusy(key);
    setError('');
    try {
      const response = await CommanServices.recordIncidentResourceUsage(
        incident.id,
        link.resourceId,
        {
          action_card_id: editingItem.id,
          task_id: taskId || null,
          operation,
          quantity: 1,
          event_id: eventId,
        }
      );
      if (!response?.status) {
        setError(String(response?.message || 'Resource usage could not be updated.'));
        return;
      }
      toast.success(operation.startsWith('end_') ? 'Resource returned' : 'Resource usage recorded');
      await loadAvailableResources();
      window.dispatchEvent(new Event('timelineRefresh'));
    } catch (usageError) {
      setError('Resource usage could not be updated.');
    } finally {
      setResourceUseBusy('');
    }
  };

  useEffect(() => { loadAvailableResources(); }, [incident?.id]);
  useEffect(() => {
    const handler = () => loadAvailableResources();
    window.addEventListener('timelineRefresh', handler);
    return () => window.removeEventListener('timelineRefresh', handler);
  }, [incident?.id]);

  // Link a resource to a task — or to the WHOLE action card (taskId '__card__').
  // Strict accounting: the requested quantity is capped at the live remaining
  // units; when nothing is left the link is refused.
  const handleLinkResource = (taskId) => {
    if (!pickResourceId) return;
    const r = availableResources.find((x) => x.id === pickResourceId);
    if (!r) return;
    const avail = liveAvail(r);
    if (avail <= 0) {
      toast.error(`No units of "${r.category}" left to link — ${r.quantity} total, all already linked.`);
      return;
    }
    let qty = Math.max(1, Number(pickQty) || 1);
    if (qty > avail) {
      qty = avail;
      toast.warning(`Only ${avail} unit${avail === 1 ? '' : 's'} of "${r.category}" available — linked ${avail}.`);
    }
    const applyLink = (existing = []) => {
      const idx = existing.findIndex((lr) => lr.resourceId === r.id);
      return idx >= 0
        ? existing.map((lr, i) => (i === idx ? { ...lr, quantity: (Number(lr.quantity) || 0) + qty } : lr))
        : [...existing, {
          resourceId: r.id,
          category: r.category,
          quantity: qty,
          availability: r.availability,
          cost: r.cost,
          replenishEvery: r.replenishEvery,
          replenishUnit: r.replenishUnit,
          note: r.note || '',
        }];
    };
    if (taskId === '__card__') {
      setCardLinkedResources((prev) => applyLink(Array.isArray(prev) ? prev : []));
    } else {
      setFormData((prev) => ({
        ...prev,
        tasks: prev.tasks.map((t) => (t.taskId !== taskId ? t : { ...t, linkedResources: applyLink(t.linkedResources) })),
      }));
    }
    setPickResourceId('');
    setPickQty(1);
    setLinkPickerTask(null);
  };

  const handleUnlinkResource = (taskId, resourceId) => {
    if (taskId === '__card__') {
      setCardLinkedResources((prev) => (prev || []).filter((lr) => lr.resourceId !== resourceId));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) =>
        t.taskId === taskId
          ? { ...t, linkedResources: (t.linkedResources || []).filter((lr) => lr.resourceId !== resourceId) }
          : t
      ),
    }));
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await CommanServices.getactioncardsData();
        if (active && res?.status) {
          const data = await res.data;
          setSavedActionCards(Array.isArray(data) ? data : (data?.items || []));
        }
      } catch (e) { /* best-effort */ }
    })();
    return () => { active = false; };
  }, []);

  const applySavedActionCard = (card) => {
    setActionCardName(card.card_name || card.name || 'Action Card');
    const tasks = (card.action_tasks || [])
      .slice()
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
      .map((t, i) => ({
        taskId: `task_${Date.now()}_${i}`,
        taskText: t.task_text || t.text || '',
        order: i,
        sla: t.sla_minutes ?? t.sla ?? 0,
        isCompleted: false,
        completedBy: null,
        completedAt: null,
        notes: '',
        notApplicable: false,
      }))
      .filter((t) => t.taskText.trim());
    setFormData((prev) => ({ ...prev, tasks }));
  };

  // Requested-stage exercises author injects exactly like the Scenario page (full role-based layout).
  // Role-planning mode: scenario page, exercise at Requested, or a REAL incident
  // reviewing its scenario plan (cards join the plan and fire on activation).
  const isScenario = !!incident?.is_scenario || (!!incident?.is_exercise && String(incident?.status || '').toLowerCase() === 'requested') || (!incident?.is_exercise && incident?.plan_state === 'planned');
  const realPlanReview = !incident?.is_exercise && incident?.plan_state === 'planned';
  // + button on a REAL incident: recipients first, live send, no planning blocks.
  const realCreate = !isEditMode && !isImmediateTask && !incident?.is_exercise && !incident?.is_scenario;
  const planCardEdit = !!editingItem && editingItem?.plan_state === 'planned' && !incident?.is_exercise;
  const scenarioPulledEdit = canOverrideScenarioRoles(incident, editingItem);
  // Geo-fence (and similar flows) hand in pre-selected recipients for the picker.
  const [pickerSelections, setPickerSelections] = useState(() => (Array.isArray(prefilledSelections) ? prefilledSelections : []));
  const geoFenceRecipientCreate = shouldUseGeoFenceRecipientCreate(editingItem, geoFenceTargeting, pickerSelections);
  const recipientCreate = realCreate || geoFenceRecipientCreate;
  const geoFenceMeta = geoFenceRecipientCreate ? {
    source: 'geo_fence',
    geoFenceTargeting,
    recipientIds: geoFenceTargeting.userIds,
  } : {};
  const isRequested = false;
  const authoring = !isEditMode || isScenario;

  // Prefill role(s) + note from the source card when authoring a conditional follow-up.
  useEffect(() => {
    if (!editingItem && conditionalContext?.sourceRoles?.length) {
      setRoles(conditionalContext.sourceRoles.map((r) => ({ ...r, sla: r.sla == null ? '' : r.sla })));
      if (conditionalContext.sourceNote) setNote(conditionalContext.sourceNote);
    }
  }, [conditionalContext, editingItem]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const isImmediateModeCard = isImmediateTask || actionCardName === 'Immediate action card' || (editingItem?.meta?.actionCardName === 'Immediate action card');

  const getActiveTaskInfo = () => {
    if (!editingItem || !editingItem.meta) return null;
    
    // Sort tasks by order
    const sortedTasks = [...formData.tasks].sort((a, b) => (a.order || 0) - (b.order || 0));
    
    // Find index of first incomplete task
    const activeIndex = sortedTasks.findIndex(t => !t.isCompleted);
    if (activeIndex === -1) return null; // All completed
    
    const activeTask = sortedTasks[activeIndex];
    
    // Determine when this task's timer started
    let startTimeStr = editingItem.meta.notifiedAt || editingItem.created_at || editingItem.timestamp;
    if (activeIndex > 0) {
      const prevTask = sortedTasks[activeIndex - 1];
      startTimeStr = prevTask.completedAt || startTimeStr;
    }
    
    if (!startTimeStr) return null;
    
    const startTime = parseTimestamp(startTimeStr);
    const durationMins = Number(activeTask.timerMinutes) || 5;
    const endTime = new Date(startTime.getTime() + durationMins * 60 * 1000);
    
    const remainingMs = endTime.getTime() - currentTime.getTime();
    
    return {
      taskId: activeTask.taskId,
      remainingMs,
      isOverdue: remainingMs < 0,
    };
  };

  const formatCountdown = (ms) => {
    const totalSecs = Math.floor(Math.abs(ms) / 1000);
    const hours = Math.floor(totalSecs / 3600);
    const minutes = Math.floor((totalSecs % 3600) / 60);
    const seconds = totalSecs % 60;
    
    const sign = ms < 0 ? '-' : '';
    const pad = (num) => String(num).padStart(2, '0');
    
    if (hours > 0) {
      return `${sign}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `${sign}${pad(minutes)}:${pad(seconds)}`;
  };

  // Update task timer minutes
  const handleUpdateTaskTimer = (taskId, newTimer) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => 
        t.taskId === taskId ? { ...t, timerMinutes: Number(newTimer) || 1 } : t
      )
    }));
  };

  const getCurrentUser = () => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      console.warn('Unable to parse current user from localStorage:', e);
      return null;
    }
  };
  
  // Add new task to the list
  const handleAddTask = () => {
    if (!newTaskText.trim()) {
      setError('Please enter task text');
      return;
    }
    
    const newTask = {
      taskId: `task_${Date.now()}`,
      taskText: newTaskText.trim(),
      order: formData.tasks.length,
      sla: 5,
      isCompleted: false,
      completedBy: null,
      completedAt: null,
      notes: '',
      notApplicable: false,
      linkedResources: [],
      ...(isImmediateTask ? { timerMinutes: Number(newTaskTimer) || 5 } : {})
    };
    
    setFormData(prev => ({
      ...prev,
      tasks: [...prev.tasks, newTask]
    }));
    
    setNewTaskText('');
    setNewTaskTimer(5);
    setError('');
  };
  
  // Remove task from the list
  const handleRemoveTask = (taskId) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.filter(t => t.taskId !== taskId)
    }));
  };
  
  // Update task text
  const handleUpdateTaskText = (taskId, newText) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => 
        t.taskId === taskId ? { ...t, taskText: newText } : t
      )
    }));
  };

  // Update per-task SLA (minutes)
  const handleUpdateTaskSla = (taskId, value) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map(t =>
        t.taskId === taskId ? { ...t, sla: value } : t
      )
    }));
  };

  // The Response Time SLA is the SUM of all task SLAs (auto-managed).
  const totalTaskSla = (formData.tasks || []).reduce((s, t) => s + (parseInt(t.sla) || 0), 0);
  useEffect(() => {
    if (Number(formData.responseTime) !== totalTaskSla) {
      setFormData(prev => ({ ...prev, responseTime: totalTaskSla }));
    }
  }, [totalTaskSla]);
  
  // Move task up
  const handleMoveTaskUp = (index) => {
    if (index === 0) return;
    setFormData(prev => {
      const newTasks = [...prev.tasks];
      [newTasks[index - 1], newTasks[index]] = [newTasks[index], newTasks[index - 1]];
      // Update order indices
      newTasks.forEach((task, idx) => {
        task.order = idx;
      });
      return { ...prev, tasks: newTasks };
    });
  };
  
  // Move task down
  const handleMoveTaskDown = (index) => {
    if (index === formData.tasks.length - 1) return;
    setFormData(prev => {
      const newTasks = [...prev.tasks];
      [newTasks[index], newTasks[index + 1]] = [newTasks[index + 1], newTasks[index]];
      // Update order indices
      newTasks.forEach((task, idx) => {
        task.order = idx;
      });
      return { ...prev, tasks: newTasks };
    });
  };
  
  // Fetch users, departments and crews for create-mode target selection
  useEffect(() => {
    const load = async () => {
      try {
        if (recipientScopeRestricted && incident?.id) {
          const scopedResponse = await CommanServices.getIncidentRecipientDirectory(incident.id);
          const scoped = normalizeIncidentRecipientDirectory(scopedResponse);
          setAppUsers(scoped.users);
          setDepartments(scoped.departments);
          setCrewOptions(scoped.crews.filter((crew) => crew?.is_active !== false));
          setDutyMap(scoped.duty);
          return;
        }
        const [uRes, dRes, tRes] = await Promise.all([
          CommanServices.getAppUsers(),
          CommanServices.departments(),
          CommanServices.getTeamsData(),
        ]);

        if (uRes?.status) {
          setAppUsers(Array.isArray(uRes.data) ? uRes.data : []);
        }
        if (dRes?.status) {
          setDepartments(Array.isArray(dRes.data) ? dRes.data : []);
        }
        if (tRes?.status) {
          const teams = Array.isArray(tRes.data) ? tRes.data : [];
          const crews = [];
          teams.forEach((team) => {
            (team.crews || []).forEach((c) => {
              if (c.id && c.is_active !== false) {
                crews.push({
                  id: c.id,
                  team_id: c.team_id || team.id,
                  crew_name: c.crew_name || 'Crew',
                  team_name: team.team_name || 'Team',
                  escalation_rules: c.escalation_rules || [],
                });
              }
            });
          });
          setCrewOptions(crews);
        }
      } catch (error) {
        console.error('Error loading Response Action selector data:', error);
      }
    };
    load();
  }, [incident?.id, recipientScopeRestricted]);

  // Initialize form data from editingItem if in edit mode
  useEffect(() => {
    if (editingItem) {
      // Fetch fresh data from api
      const fetchFreshData = async () => {
        if (editingItem?.meta?.isVirtual) {
          console.log('ℹ️ Virtual item detected, skipping fresh fetch');
          initializeFormFromItem(editingItem);
          return;
        }
        try {
          const response = await CommanServices.getincidentTimeline(incident.id);
          if (response.status) {
            const timelineItems = await response.data;
            const freshItem = timelineItems.find(item => item.id === editingItem.id);
            
            if (freshItem) {
              console.log('✅ Fetched fresh Response Action item:', freshItem);
              initializeFormFromItem(freshItem);
            } else {
              console.warn('⚠️ Could not find fresh item, using passed editingItem');
              initializeFormFromItem(editingItem);
            }
          } else {
            console.warn('⚠️ Failed to fetch timeline, using passed editingItem');
            initializeFormFromItem(editingItem);
          }
        } catch (error) {
          console.error('⚠️ Error fetching fresh data:', error);
          initializeFormFromItem(editingItem);
        }
      };

      fetchFreshData();
    } else {
      setIsEditMode(false);
      setPerformedByUser(null);
      setIsPerformed(false);
      setSelectedUserIds(prefilledUserIds || []);
      setSelectedDepartmentIds([]);
      setSelectedCrewIds([]);
      setTargetTab('users');
      setUserSearch('');
      setActionCardName(prefilledName || '');
    }
  }, [editingItem, prefilledName, prefilledUserIds]);

  // Reflect an expired-task state locally in the modal only. We intentionally do NOT silently
  // write to the DB or dispatch a 'timelineRefresh' here — that made the whole timeline churn in
  // the background the moment a handover modal was opened. The expired state is persisted on Save.
  useEffect(() => {
    if (!isEditMode || !editingItem || !isImmediateModeCard || !formData.tasks.length) return;

    const computed = computeTasksWithState(formData.tasks, editingItem.meta?.notifiedAt || editingItem.timestamp, currentTime);
    const toUpdate = computed.find(t => t.state === 'failed' && !t.failedSLA);

    if (toUpdate) {
      setFormData(prev => ({
        ...prev,
        tasks: prev.tasks.map(t =>
          t.taskId === toUpdate.taskId ? { ...t, failedSLA: true, isCompleted: false, notes: 'Timer Expired (Missed SLA)' } : t
        )
      }));
    }
  }, [currentTime, isEditMode, editingItem, isImmediateModeCard, formData.tasks, incident.id]);

  // Helper function to initialize form from timeline item
  const initializeFormFromItem = (item) => {
    setIsEditMode(true);
    const meta = item.meta || {};
    setRoleOverrideMeta(meta);
    
    console.log('🔍 Initializing Response Action form from item, meta:', meta);
    
    // Check if already performed
    const hasPerformed = !!meta.performedAt;
    setIsPerformed(hasPerformed);
    
    // Set action card name
    setActionCardName(meta.actionCardName || 'Response Action');
    setEscalation(meta.escalation || null);
    setRoles(Array.isArray(meta.scenarioRoles) && meta.scenarioRoles.length
      ? markScenarioRolesForOverride(meta.scenarioRoles.map((r) => ({ ...r, sla: r.sla == null ? '' : r.sla })), canOverrideScenarioRoles(incident, item))
      : [blankRole()]);
    setNote(meta.assignmentNote || '');
    
    const newFormData = {
      performedAt: meta.performedAt || null,
      performanceStatus: meta.performanceStatus || 'Not Performed',
      tasks: meta.tasks || [],
      responseTime: meta.sla?.withinMinutes ?? '',
    };
    
    setFormData(newFormData);
    
    // Performed by user in edit mode
    if (meta.performedBy) {
      setPerformedByUser(meta.performedBy);
    } else {
      setPerformedByUser(null);
    }
  };

  // Handle task completion toggle
  const handleTaskToggle = (taskId) => {
    if (isImmediateModeCard) {
      const computed = computeTasksWithState(formData.tasks, editingItem?.meta?.notifiedAt || editingItem?.timestamp, currentTime);
      const target = computed.find(t => t.taskId === taskId);
      if (target && (target.state === 'failed' || target.failedSLA)) {
        console.warn('⚠️ Cannot complete an expired task!');
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map(task => {
        if (task.taskId === taskId) {
          const newIsCompleted = !task.isCompleted;
          return {
            ...task,
            isCompleted: newIsCompleted,
            completedBy: newIsCompleted ? (performedByUser?.userName || 'User') : null,
            completedAt: newIsCompleted ? new Date().toISOString() : null
          };
        }
        return task;
      })
    }));
  };


  // Handle "Mark as Performed" button click
  const handleMarkAsPerformed = async () => {
    if (isPerformed) {
      setError('This response action has already been performed.');
      return;
    }
    setPerformButtonLoading(true);
    setError('');

    try {
      // Set performedAt timestamp
      const performedAtTime = new Date().toISOString();
      setFormData(prev => ({
        ...prev,
        performedAt: performedAtTime,
        performanceStatus: 'Performed'
      }));
      setIsPerformed(true);
      
      console.log('✅ Marked as Performed at:', performedAtTime);
    } catch (error) {
      console.error('Error marking as performed:', error);
      setError('Failed to mark as performed');
    } finally {
      setPerformButtonLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    const allowed = Array.isArray(allowedRecipientUserIds)
      ? new Set(allowedRecipientUserIds.map(String))
      : null;
    const scopedUsers = allowed ? appUsers.filter((u) => allowed.has(String(u.id))) : appUsers;
    if (!q) return scopedUsers;
    return scopedUsers.filter((u) =>
      String(u.name || '').toLowerCase().includes(q) ||
      String(u.email || '').toLowerCase().includes(q) ||
      String(u.position || '').toLowerCase().includes(q)
    );
  }, [appUsers, userSearch, allowedRecipientUserIds]);
  const scopedDepartments = useMemo(() => {
    if (!Array.isArray(allowedRecipientDepartmentIds)) return departments;
    const allowed = new Set(allowedRecipientDepartmentIds.map(String));
    return departments.filter((department) => (
      allowed.has(String(department.id || ''))
      || allowed.has(String(department.department_name || ''))
    ));
  }, [departments, allowedRecipientDepartmentIds]);
  const scopedCrews = useMemo(() => {
    if (!Array.isArray(allowedRecipientTeamIds) && !Array.isArray(allowedRecipientCrewIds)) return crewOptions;
    const allowedTeams = new Set((allowedRecipientTeamIds || []).map(String));
    const allowedCrews = new Set((allowedRecipientCrewIds || []).map(String));
    return crewOptions.filter((crew) => (
      allowedCrews.has(String(crew.id || ''))
      || allowedTeams.has(String(crew.team_id || ''))
    ));
  }, [crewOptions, allowedRecipientTeamIds, allowedRecipientCrewIds]);

  useEffect(() => {
    if (isEditMode) return;
    if (Array.isArray(allowedRecipientUserIds)) {
      const allowed = new Set(allowedRecipientUserIds.map(String));
      setSelectedUserIds((current) => current.filter((id) => allowed.has(String(id))));
    }
    if (Array.isArray(allowedRecipientDepartmentIds)) {
      const allowed = new Set(allowedRecipientDepartmentIds.map(String));
      setSelectedDepartmentIds((current) => current.filter((id) => allowed.has(String(id))));
    }
    if (Array.isArray(allowedRecipientTeamIds) || Array.isArray(allowedRecipientCrewIds)) {
      const allowedTeams = new Set((allowedRecipientTeamIds || []).map(String));
      const allowedCrews = new Set((allowedRecipientCrewIds || []).map(String));
      setSelectedCrewIds((current) => current.filter((id) => {
        if (allowedCrews.has(String(id))) return true;
        const crew = crewOptions.find((row) => String(row.id) === String(id));
        return !!crew && allowedTeams.has(String(crew.team_id || ''));
      }));
    }
  }, [isEditMode, allowedRecipientUserIds, allowedRecipientDepartmentIds, allowedRecipientTeamIds, allowedRecipientCrewIds, crewOptions]);

  const toggleUser = (id) => {
    setSelectedUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleDepartment = (id) => {
    setSelectedDepartmentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleCrew = (id) => {
    setSelectedCrewIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const resolveCrewUserIds = () => {
    const out = new Set();
    selectedCrewIds.forEach((cid) => {
      const crew = crewOptions.find((c) => c.id === cid);
      if (!crew) return;
      (crew.escalation_rules || []).forEach((r) => {
        const et = r?.escalated_to;
        const arr = Array.isArray(et) ? et : [et];
        arr.filter(Boolean).forEach((target) => {
          const s = String(target).trim().toLowerCase();
          const byId = appUsers.find((u) => String(u.id || '').trim().toLowerCase() === s);
          if (byId?.id) out.add(byId.id);
          const byEmail = appUsers.find((u) => String(u.email || '').trim().toLowerCase() === s);
          if (byEmail?.id) out.add(byEmail.id);
          const byAccount = appUsers.find((u) => String(u.user_account || u.name || '').trim().toLowerCase() === s);
          if (byAccount?.id) out.add(byAccount.id);
        });
      });
      appUsers.forEach((u) => {
        if (String(u.crew_id || '').trim() === cid) out.add(u.id);
      });
    });
    return out;
  };

  const resolveDepartmentUserIds = () => {
    const out = new Set();
    selectedDepartmentIds.forEach((did) => {
      appUsers.forEach((u) => {
        const vals = [...(u.departments_units || []), u.department_unit, u.department].filter(Boolean);
        if (vals.includes(did)) out.add(u.id);
      });
      const department = departments.find((row) => (
        String(row.id || '') === String(did)
        || String(row.department_name || '') === String(did)
      ));
      (department?.members || []).forEach((member) => {
        const userId = typeof member === 'object' ? (member.id || member.user_id) : member;
        if (userId) out.add(String(userId));
      });
    });
    return out;
  };

  const resolveRecipients = () => {
    const ids = new Set(selectedUserIds);
    resolveDepartmentUserIds().forEach((x) => ids.add(x));
    resolveCrewUserIds().forEach((x) => ids.add(x));
    return appUsers
      .filter((u) => ids.has(u.id) && (
        !Array.isArray(allowedRecipientUserIds)
        || allowedRecipientUserIds.map(String).includes(String(u.id))
      ))
      .map((u) => ({
        userId: u.id,
        userName: u.name || u.email || 'User',
        email: u.email || '',
        crewName: u.crewName || 'Manual',
        teamName: u.teamName || 'Manual',
      }));
  };

  const buildAssignmentSummary = (recipients) => {
    const selectedUsers = appUsers.filter((u) => selectedUserIds.includes(u.id));
    const selectedCrews = crewOptions.filter((c) => selectedCrewIds.includes(c.id));
    const selectedDepartments = departments.filter((d) =>
      selectedDepartmentIds.includes(d.id || d.department_name)
    );

    let label = 'Mixed Targets';
    if (selectedUsers.length && !selectedCrews.length && !selectedDepartments.length) {
      label = selectedUsers.length === 1
        ? `User: ${selectedUsers[0].name || selectedUsers[0].email || 'User'}`
        : `Users (${selectedUsers.length})`;
    } else if (selectedCrews.length && !selectedUsers.length && !selectedDepartments.length) {
      label = selectedCrews.length === 1
        ? `Crew: ${selectedCrews[0].crew_name || 'Crew'}`
        : `Crews (${selectedCrews.length})`;
    } else if (selectedDepartments.length && !selectedUsers.length && !selectedCrews.length) {
      label = selectedDepartments.length === 1
        ? `Department: ${selectedDepartments[0].department_name || 'Department'}`
        : `Departments (${selectedDepartments.length})`;
    }

    return {
      label,
      assignedUserCount: recipients.length,
      users: selectedUsers.map((u) => ({
        id: u.id,
        name: u.name || u.email || 'User',
      })),
      crews: selectedCrews.map((c) => ({
        id: c.id,
        name: c.crew_name || 'Crew',
        teamName: c.team_name || 'Team',
      })),
      departments: selectedDepartments.map((d) => ({
        id: d.id || d.department_name,
        name: d.department_name || d.name || 'Department',
      })),
    };
  };

  // Handle form submission (Save)
  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      if (isScenario && (planCardEdit || !realPlanReview)) {
        if (!rolesValid(roles, incident?.is_scenario ? scenarioStaffingDefaults : null)) {
          throw new Error('Add at least one role.');
        }
        const overridePatch = scenarioPulledEdit
          ? buildScenarioRoleOverridePatch(roleOverrideMeta, roles)
          : null;
        if (overridePatch?.error) throw new Error(overridePatch.error);
        const clean = overridePatch?.scenarioRoles || cleanRoles(roles);
        const meta = {
          actionCardName: actionCardName || 'Manual',
          scenarioRoles: clean,
          ...(overridePatch ? {
            roleAssignments: overridePatch.roleAssignments,
            history: overridePatch.history,
            roleOverrideHistory: overridePatch.roleOverrideHistory,
          } : {}),
          assignmentNote: note || '',
          tasks: formData.tasks,
          performanceStatus: 'Not Performed',
          // Empty or 0 = no SLA entered → store none (no 5-minute fallback).
          sla: parseInt(formData.responseTime) > 0 ? { withinMinutes: parseInt(formData.responseTime) } : null,
          escalation: escalation || null,
          manuallyCreated: true,
          addedDuringActive: isActiveExercise,
          ...(!overridePatch ? { history: Array.isArray(editingItem?.meta?.history) ? editingItem.meta.history : [] } : {}),
          ...(conditionalContext?.conditional ? { conditional: conditionalContext.conditional } : {}),
        };
        const base = {
          incident_id: incident.id,
          group: 'response_action',
          timestamp: new Date().toISOString(),
          actor: { id: 'role', name: rolesSummary(clean), type: 'role' },
          title: `${actionCardName || 'Manual'}`,
          details: rolesSummary(clean),
          chips: [],
          attachments: [],
          meta,
        };
        let response;
        if (isEditMode && editingItem) {
          response = await CommanServices.updatePutIncidentTimeline(incident.id, editingItem.id, {
            ...editingItem,
            ...base,
            meta: { ...(editingItem.meta || {}), ...meta },
          });
        } else {
          response = await CommanServices.addincidentTimeline(incident.id, base);
        }
        if (!timelineWriteSucceeded(response)) throw new Error(response?.message || 'Failed to save response action');
        onSuccess && onSuccess();
        onClose();
        return;
      }

      // Empty or 0 = no SLA entered → none (no 5-minute fallback).
      const slaWithinMinutes = parseInt(formData.responseTime) || null;

      const recipients = isEditMode
        ? [performedByUser].filter(Boolean)
        : recipientCreate
          ? (await resolveSharedRecipients(pickerSelections, {
              incidentId: incident?.id,
              restricted: [
                allowedRecipientUserIds,
                allowedRecipientDepartmentIds,
                allowedRecipientTeamIds,
                allowedRecipientCrewIds,
              ].some(Array.isArray),
            })).map((u) => ({
              userId: u.userId, userName: u.userName, email: u.email || '',
              crewName: 'Manual', teamName: 'Manual', _role: (u.role || '').trim(),
            }))
          : resolveRecipients();
      const pickerRoleAssignments = {};
      for (const r of recipients) {
        if (r._role) {
          (pickerRoleAssignments[r._role] = pickerRoleAssignments[r._role] || []).push({ type: 'user', id: String(r.userId), name: r.userName });
        }
      }
      // In edit/handover mode the card already carries its assignments (roleAssignments/scenarioRoles),
      // so we must NOT block saving task progress / "Mark as Performed" when no recipient is re-selected.
      if (!isEditMode && !recipients.length) {
        throw new Error('Select at least one target (users, departments, or crews).');
      }

      // Check if performed within SLA (only for edit mode with performedAt)
      let slaMet = null;
      let actualMinutes = null;
      
      if (isEditMode && formData.performedAt && editingItem?.meta?.notifiedAt) {
        const notifiedAt = parseTimestamp(editingItem.meta.notifiedAt);
        const performedAt = parseTimestamp(formData.performedAt);
        const withinMinutes = editingItem.meta.sla?.withinMinutes || slaWithinMinutes;

        if (notifiedAt && performedAt) {
          actualMinutes = (performedAt - notifiedAt) / (1000 * 60); // Convert ms to minutes
          // No SLA → no deadline → can never be late.
          slaMet = withinMinutes == null ? true : actualMinutes <= withinMinutes;
        }
      }
      
      // Build update payload
      const updatePayload = isEditMode ? {
        ...editingItem,
        timestamp: editingItem.timestamp, // Preserve original timestamp
        meta: {
          ...editingItem.meta,
          performedAt: formData.performedAt,
          performedBy: performedByUser || recipients[0] || editingItem?.meta?.performedBy || {},
          performanceStatus: formData.performanceStatus,
          tasks: formData.tasks,
          linkedResources: cardLinkedResources,
          sla: {
            ...(editingItem.meta?.sla || {}),
            met: slaMet,
            actualMinutes: actualMinutes
          },
          escalation: escalation || null,
          history: [
            ...(editingItem.meta?.history || []),
            {
              timestamp: new Date().toISOString(),
              actor: recipients[0]?.userName || 'Unknown',
              action: 'Updated Response Action',
              changes: {
                performanceStatus: formData.performanceStatus,
                tasksCompleted: formData.tasks.filter(t => t.isCompleted).length
              }
            }
          ]
        }
      } : null;

      // Make api request

      if (isEditMode) {
        if (editingItem?.meta?.isVirtual) {
          // Handle virtual item update by patching incident.planning_tasks
          const { planId, originalTaskId } = editingItem.meta;
          const isPerformed = formData.performanceStatus === 'Performed';
          const currentUser = getCurrentUser();
          const performerName = currentUser?.name || currentUser?.full_name || currentUser?.email || 'User';

          const updatedPlanningTasks = (incident?.planning_tasks || []).map(plan => {
            if (plan.id === planId || plan._id === planId) {
              const updatedTasks = (plan.tasks || []).map(task => {
                if (task.id === originalTaskId || task.title === originalTaskId) {
                  // Update completion based on modal's tasks (virtual items have 1 task)
                  const modalTask = formData.tasks[0];
                  const completed = isPerformed || modalTask?.isCompleted;
                  return { 
                    ...task, 
                    isCompleted: completed,
                    completedAt: completed ? (formData.performedAt || modalTask?.completedAt || new Date().toISOString()) : null,
                    completedBy: completed ? (performerName || modalTask?.completedBy) : null
                  };
                }
                return task;
              });
              return { ...plan, tasks: updatedTasks };
            }
            return plan;
          });

          const res = await CommanServices.updatePatchIncidents(incident.id, { planning_tasks: updatedPlanningTasks });
          if (!timelineWriteSucceeded(res)) {
            throw new Error('Failed to update planning task');
          }
        } else {
          const res = await CommanServices.updatePutIncidentTimeline(incident.id, editingItem.id, updatePayload)
          if (!res.status) {
            throw new Error(res.message || 'Failed to save status update');
          }
        }
      } else {
        let assignmentSummary = buildAssignmentSummary(recipients);
        if (recipientCreate && pickerSelections.length) {
          assignmentSummary = {
            ...assignmentSummary,
            label: pickerSelections.map((x) => x.name).join(', '),
            assignedUserCount: recipients.length,
          };
        }
        const primaryRecipient = recipients[0] || {};
        const currentUser = getCurrentUser();
        const creatorUserId = currentUser?.id || currentUser?._id || null;
        const creatorUserName =
          currentUser?.name ||
          currentUser?.full_name ||
          currentUser?.username ||
          currentUser?.email ||
          'Admin';
        const isMultiTargetCard = Number(assignmentSummary.assignedUserCount || 0) > 1;
        const myTrackUserIds = Array.from(
          new Set([
            ...recipients.map((r) => r.userId).filter(Boolean),
            ...(isMultiTargetCard && creatorUserId ? [creatorUserId] : []),
          ])
        );
        const createPayload = {
          incident_id: incident.id,
          group: 'response_action',
          timestamp: new Date().toISOString(),
          actor: {
            id: primaryRecipient.userId || 'manual_user',
            name: primaryRecipient.userName || 'Manual User',
            type: 'user',
          },
          title: `${actionCardName || 'Manual'}`,
          details: `${assignmentSummary.label} + (${assignmentSummary.assignedUserCount} assigned users)`,
          chips: ['Pending', 'Manually Created'],
          attachments: [],
          meta: {
            actionCardId: null,
            actionCardName: actionCardName || 'Manual',
            notifiedAt: new Date().toISOString(),
            performedAt: formData.performedAt,
            performedBy: primaryRecipient,
            tasks: formData.tasks,
            linkedResources: cardLinkedResources,
            performanceStatus: formData.performanceStatus,
            assignmentSummary,
            assignedUserIds: recipients.map((r) => r.userId).filter(Boolean),
            assignees: recipients.map(({ _role, ...rest }) => rest),
            ...(Object.keys(pickerRoleAssignments).length ? { roleAssignments: pickerRoleAssignments } : {}),
            ...(recipientCreate && pickerSelections.some((x) => x.type !== 'user')
              ? { sourceGroups: pickerSelections.filter((x) => x.type !== 'user').map((x) => ({ type: x.type, name: x.name })) }
              : {}),
            createdBy: {
              userId: creatorUserId,
              userName: creatorUserName,
              email: currentUser?.email || '',
            },
            ownership: {
              isMultiTarget: isMultiTargetCard,
              myTrackUserIds,
            },
            sla: slaWithinMinutes == null ? null : {
              withinMinutes: slaWithinMinutes,
              met: null,
              actualMinutes: null,
            },
            escalation: escalation || null,
            autoCreated: false,
            isSuperseded: false,
            manuallyCreated: true,
            addedDuringActive: isActiveExercise,
            history: [],
            ...geoFenceMeta,
          },
        };
        const response = await CommanServices.addincidentTimeline(incident.id, createPayload);
        if (!timelineWriteSucceeded(response)) {
          throw new Error('Failed to create response action');
        }

        // A manual card created on a real incident is also saved to the Action
        // Cards module as a reference (auto_trigger off, no conditions), like
        // resources. Duplicate names are rejected by the backend — best-effort.
        if (realCreate && isWebAdmin(currentUser) && (actionCardName || '').trim()) {
          try {
            const catalogRes = await CommanServices.addactioncards({
              card_name: actionCardName.trim(),
              description: `Created manually during incident ${incident.id}`,
              incident_conditions: [],
              action_tasks: formData.tasks
                .filter((t) => (t.taskText || '').trim())
                .map((t, i) => ({ task_text: t.taskText.trim(), order_index: i })),
              auto_trigger: false,
              is_active: true,
            });
            if (catalogRes?.status) toast.success('New action card saved to the Action Cards module too');
          } catch (e) { /* module save is best-effort — the incident card is already created */ }
        }

        // Auto-notify the responder for immediate tasks
        if (isImmediateTask && recipients.length > 0) {
          try {
            await Promise.all(
              recipients.map(async (r) => {
                if (r.userId) {
                  console.log(`🔔 Auto-notifying immediate task recipient: ${r.userName}`);
                  await CommanServices.notificationMaunal(incident.id, {
                    user_ids: [r.userId],
                    department_ids: [],
                    crew_ids: [],
                    force: true,
                    is_attention_only: true
                  });
                }
              })
            );
            console.log('✅ Auto-notified responder(s) successfully');
          } catch (notifyErr) {
            console.error('⚠️ Failed to auto-notify responder(s):', notifyErr);
          }
        }
      }

      console.log('✅ Response Action saved successfully');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving Response Action:', error);
      setError(error.message || 'Failed to save Response Action');
    } finally {
      setLoading(false);
    }
  };

  const getSLAMinutes = () => {
    return parseInt(formData.responseTime) || 0;
  };

  const renderLinkedResource = (link, taskId, closed, onUnlink) => {
    const resource = resourceForLink(link);
    const availability = resource?.availability || 'once';
    const events = contextUsageEvents(resource, taskId);
    const nowMs = currentTime.getTime();
    const activeTimedEvent = events.find((event) => (
      event.kind === 'timed_use' &&
      event.status === 'active' &&
      new Date(event.endsAt || 0).getTime() > nowMs
    ));
    const activeReusableEvents = events.filter((event) => (
      event.kind === 'reusable_use' && event.status === 'active'
    ));
    const activeReusableEvent = activeReusableEvents[0];
    const activeTimedQuantity = events.reduce((sum, event) => (
      event.kind === 'timed_use' &&
      event.status === 'active' &&
      new Date(event.endsAt || 0).getTime() > nowMs
        ? sum + (Number(event.quantity) || 0)
        : sum
    ), 0);
    const contextCost = events.reduce(
      (sum, event) => sum + (Number(event.unitCost) || 0) * (Number(event.quantity) || 0),
      0
    );
    const expiredActiveQuantity = (resource?.usageEvents || []).reduce((sum, event) => (
      event?.kind === 'timed_use' &&
      event?.status === 'active' &&
      new Date(event?.endsAt || 0).getTime() <= nowMs
        ? sum + (Number(event?.quantity) || 0)
        : sum
    ), 0);
    const availableForTimedUse = (
      Number(resource?.usageSummary?.availableQuantity ?? resource?.quantity ?? 0) + expiredActiveQuantity
    ) >= 1 && activeTimedQuantity < Number(link.quantity || 1);
    const availableForReusableUse = Number(
      resource?.usageSummary?.availableQuantity ?? resource?.quantity ?? 0
    ) >= 1;
    const label = resource?.category || link.category || 'Resource';
    const busyPrefix = `${taskId || 'card'}:${link.resourceId}:`;
    const expectedKind = availability === 'reuse' ? 'reusable_use' : availability === 'time' ? 'timed_use' : 'single_use';
    const usedQuantity = events.reduce((sum, event) => (
      event.kind === expectedKind || (expectedKind === 'single_use' && event.kind === 'legacy_single_use')
        ? sum + (Number(event.quantity) || 0)
        : sum
    ), 0);
    const linkedQuantity = Number(link.quantity) || 0;
    const singleUseAllocationConsumed = usedQuantity >= linkedQuantity;
    const availabilityLabel = availability === 'time'
      ? replenishLabel(resource)
      : AVAILABILITY_SHORT[availability] || availability;
    return (
      <span key={link.resourceId} className="inline-flex flex-wrap items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
        <Boxes className="h-3 w-3" />
        {label} ×{link.quantity} · {availabilityLabel}
        {contextCost > 0 && <span className="rounded bg-white/80 px-1.5 py-0.5 text-emerald-700">{formatResourceMoney(contextCost)} used</span>}
        {authoring ? (
          <button type="button" onClick={onUnlink} className="hover:text-indigo-900" aria-label={`Unlink ${label}`}>
            <X className="h-3 w-3" />
          </button>
        ) : availability === 'once' && !closed ? (
          <button
            type="button"
            onClick={() => handleResourceUsage(taskId, link, 'record_single_use')}
            disabled={resourceUseBusy.startsWith(busyPrefix) || singleUseAllocationConsumed}
            className="ml-1 rounded bg-emerald-600 px-2 py-0.5 text-white disabled:opacity-50"
          >
            {singleUseAllocationConsumed ? 'Used' : 'Use'}
          </button>
        ) : availability === 'reuse' ? (
          <>
            {activeReusableEvent && (
              <button
                type="button"
                onClick={() => handleResourceUsage(taskId, link, 'end_reusable_use', activeReusableEvent.id)}
                disabled={resourceUseBusy.startsWith(busyPrefix)}
                className="ml-1 rounded bg-slate-600 px-2 py-0.5 text-white disabled:opacity-50"
              >
                Return
              </button>
            )}
            {!closed && (
              <button
                type="button"
                onClick={() => handleResourceUsage(taskId, link, 'start_reusable_use')}
                disabled={resourceUseBusy.startsWith(busyPrefix) || !availableForReusableUse}
                className="ml-1 rounded bg-emerald-600 px-2 py-0.5 text-white disabled:opacity-50"
              >
                Use
              </button>
            )}
          </>
        ) : availability === 'time' ? (
          <>
            {activeTimedEvent && (
              <button
                type="button"
                onClick={() => handleResourceUsage(taskId, link, 'end_timed_use', activeTimedEvent.id)}
                disabled={resourceUseBusy.startsWith(busyPrefix)}
                className="ml-1 rounded bg-slate-600 px-2 py-0.5 text-white disabled:opacity-50"
              >
                Return
              </button>
            )}
            {!closed && (
              <button
                type="button"
                onClick={() => handleResourceUsage(taskId, link, 'start_timed_use')}
                disabled={resourceUseBusy.startsWith(busyPrefix) || !availableForTimedUse}
                className="ml-1 rounded bg-amber-600 px-2 py-0.5 text-white disabled:opacity-50"
              >
                Use
              </button>
            )}
          </>
        ) : events.length > 0 ? (
          <span className="text-emerald-700">✓ recorded</span>
        ) : null}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode ? `Edit Response Action` : 'Create Response Action'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close response action"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {isScenario && !recipientCreate && !isImmediateTask && (
            <ScenarioRoleField roles={roles} setRoles={setRoles} note={note} setNote={setNote} slaValue={formData.responseTime} onSlaChange={(v) => setFormData((prev) => ({ ...prev, responseTime: v }))} slaLabel="Response Time SLA (minutes)" slaReadOnly slaHint="Auto-calculated as the sum of all task SLAs below." hideIntro={planCardEdit} roleCatalog={incident?.is_scenario ? scenarioStaffingDefaults : null} onRoleCatalogUpsert={onRoleCatalogUpsert} lockScenarioDefaultRoles={scenarioPulledEdit} roleAssignments={roleOverrideMeta?.roleAssignments || {}} />
          )}
          {/* Assigned to — recipients come FIRST when creating on a real incident */}
          {(!isScenario || recipientCreate) && (
          <div>
            <Label>Assigned to</Label>
            {isEditMode ? (
              <div className="mt-1 p-3 bg-blue-50 border border-blue-200 rounded">
                {(performedByUser?.userName || performedByUser?.name) ? (
                  <>
                    <div className="font-medium text-gray-900">{performedByUser?.userName || performedByUser?.name}</div>
                    <div className="text-sm text-gray-600">
                      {[performedByUser?.crewName, performedByUser?.teamName].filter(Boolean).join(' • ') || '—'}
                    </div>
                  </>
                ) : (() => {
                  const ra = editingItem?.meta?.roleAssignments || {};
                  const entries = Object.entries(ra).filter(([, list]) => Array.isArray(list) && list.length);
                  if (entries.length === 0) {
                    return <div className="text-sm text-gray-500">Assigned responders will appear here.</div>;
                  }
                  return (
                    <div className="space-y-1" data-testid="response-assigned-roles">
                      {entries.map(([role, list]) => (
                        <div key={role} className="text-sm">
                          <span className="font-semibold text-emerald-700">{role}:</span>{' '}
                          <span className="text-gray-800">{list.map((u) => u.name).join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ) : isImmediateTask ? (
              <div className="mt-2 p-3.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl flex items-center gap-3">
                <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold text-sm shadow-sm flex-shrink-0">
                  {(() => {
                    const match = appUsers.find(u => u.id === selectedUserIds[0]);
                    return match?.name?.charAt(0).toUpperCase() || 'U';
                  })()}
                </div>
                <div>
                  <div className="font-semibold text-slate-800 text-sm">
                    {(() => {
                      const match = appUsers.find(u => u.id === selectedUserIds[0]);
                      return match?.name || match?.email || 'Loading assigned responder...';
                    })()}
                  </div>
                  <div className="text-xs text-slate-500">
                    {(() => {
                      const match = appUsers.find(u => u.id === selectedUserIds[0]);
                      return match?.email || '';
                    })()}
                  </div>
                </div>
              </div>
            ) : recipientCreate ? (
              <div className="mt-2">
                <RecipientPicker
                  selections={pickerSelections}
                  onChange={setPickerSelections}
                  testIdPrefix="ra"
                  incidentId={incident?.id}
                  onlineUserIds={onlineUserIds}
                  presenceByUserId={presenceByUserId}
                  variant="notification"
                  allowedUserIds={allowedRecipientUserIds}
                  allowedDepartmentIds={allowedRecipientDepartmentIds}
                  allowedTeamIds={allowedRecipientTeamIds}
                  allowedCrewIds={allowedRecipientCrewIds}
                />
              </div>
            ) : (
              <div className="mt-2 space-y-3">
                <p className="text-xs text-gray-600">Select one or many users / departments / crews.</p>
                <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 text-xs">
                  <button type="button" className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-md ${targetTab === 'users' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`} onClick={() => setTargetTab('users')}>
                    <Users className="h-3.5 w-3.5" /> Users
                  </button>
                  <button type="button" className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-md ${targetTab === 'departments' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`} onClick={() => setTargetTab('departments')}>
                    <Building2 className="h-3.5 w-3.5" /> Departments
                  </button>
                  <button type="button" className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-md ${targetTab === 'crews' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`} onClick={() => setTargetTab('crews')}>
                    <UsersRound className="h-3.5 w-3.5" /> Crews
                  </button>
                </div>

                {targetTab === 'users' && (
                  <div className="space-y-2">
                    <input type="search" placeholder="Search users…" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                    <div className="border border-gray-200 rounded-md max-h-44 overflow-y-auto divide-y">
                      {filteredUsers.map((u) => (
                        <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                          <Checkbox checked={selectedUserIds.includes(u.id)} onCheckedChange={() => toggleUser(u.id)} />
                          <span className="flex-1">{u.name || u.email}</span>
                          {onlineUserIds?.has?.(String(u.id)) && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700" title="Connected to the mobile app now">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Online now
                            </span>
                          )}
                          <DutyBadge status={dutyMap[u.id]?.status} />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {targetTab === 'departments' && (
                  <div className="border border-gray-200 rounded-md max-h-44 overflow-y-auto divide-y">
                    {scopedDepartments.map((d) => (
                      <label key={d.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                        <Checkbox checked={selectedDepartmentIds.includes(d.id)} onCheckedChange={() => toggleDepartment(d.id)} />
                        <span>{d.department_name || d.name || d.id}</span>
                      </label>
                    ))}
                  </div>
                )}
                {targetTab === 'crews' && (
                  <div className="border border-gray-200 rounded-md max-h-44 overflow-y-auto divide-y">
                    {scopedCrews.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                        <Checkbox checked={selectedCrewIds.includes(c.id)} onCheckedChange={() => toggleCrew(c.id)} />
                        <span>{c.crew_name}<span className="text-xs text-gray-500"> — {c.team_name}</span></span>
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-700">
                  Selection summary: {selectedUserIds.length} user(s), {selectedDepartmentIds.length} department(s), {selectedCrewIds.length} crew(s).
                </p>
              </div>
            )}
          </div>
          )}

          {/* Action Card Name */}
          {authoring && (
            <div className="space-y-2">
              {savedActionCards.length > 0 && !isImmediateTask && (
                <div className="relative">
                  <Label htmlFor="savedActionCard">Use a saved Action Card</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                    <input
                      id="savedActionCard"
                      data-testid="saved-action-card-picker"
                      value={savedCardQuery}
                      onChange={(e) => { setSavedCardQuery(e.target.value); setSavedCardOpen(true); }}
                      onFocus={() => setSavedCardOpen(true)}
                      onBlur={() => setTimeout(() => setSavedCardOpen(false), 150)}
                      placeholder="Search saved Action Cards…"
                      autoComplete="off"
                      className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white pl-8 pr-8 py-2"
                    />
                    <ChevronDown className={`absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none transition-transform ${savedCardOpen ? 'rotate-180' : ''}`} />
                  </div>
                  {savedCardOpen && (
                    <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg" data-testid="saved-action-card-list">
                      {savedActionCards
                        .filter((c) => !savedCardQuery.trim() || String(c.card_name || c.name || '').toLowerCase().includes(savedCardQuery.trim().toLowerCase()))
                        .map((c) => (
                          <button
                            type="button"
                            key={c.id}
                            data-testid={`saved-card-option-${c.id}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              applySavedActionCard(c);
                              setSavedCardQuery(c.card_name || c.name || '');
                              setSavedCardOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50"
                          >
                            <span className="font-medium text-gray-800">{c.card_name || c.name}</span>
                            <span className="text-xs text-gray-400 ml-1.5">({(c.action_tasks || []).length} tasks)</span>
                          </button>
                        ))}
                      {savedActionCards.filter((c) => !savedCardQuery.trim() || String(c.card_name || c.name || '').toLowerCase().includes(savedCardQuery.trim().toLowerCase())).length === 0 && (
                        <div className="px-3 py-2 text-xs text-gray-400">No matching action cards.</div>
                      )}
                    </div>
                  )}
                  <p className="text-[11px] text-gray-500 mt-1">Type to search, then pick — loads the card name and its tasks. You can still edit them below.</p>
                </div>
              )}
              <div>
                <Label htmlFor="actionCardName">Action Card Name</Label>
                <Input
                  id="actionCardName"
                  value={actionCardName}
                  onChange={(e) => setActionCardName(e.target.value)}
                  placeholder="Enter action card name..."
                  disabled={isImmediateTask}
                  className={isImmediateTask ? "bg-slate-50 font-semibold text-slate-800 border-slate-200" : ""}
                />
              </div>
            </div>
          )}


          {isRequested && (
            <RoleGuidanceBanner roles={editingItem?.meta?.scenarioRoles} note={editingItem?.meta?.assignmentNote} />
          )}

          {authoring && !isImmediateTask && !isScenario && (
            <div>
              <Label htmlFor="responseTime">Response Time SLA (minutes)</Label>
              <Input
                id="responseTime"
                type="number"
                min="0"
                value={formData.responseTime}
                readOnly
                title="Auto-calculated from the task SLAs"
                placeholder="Sum of task SLAs"
                className="no-number-spinner bg-gray-100 text-gray-600 cursor-not-allowed"
              />
              <div className="mt-1 text-sm text-gray-500">
                Auto-calculated as the sum of all task SLAs ({getSLAMinutes()} minute{getSLAMinutes() !== 1 ? 's' : ''}).
              </div>
            </div>
          )}

          {/* Resources linked to the WHOLE action card */}
          <div data-testid="card-linked-resources-section">
            <div className="flex items-center justify-between mb-1.5">
              <Label className="flex items-center gap-1.5"><Boxes className="h-4 w-4 text-indigo-600" /> Card Resources</Label>
              {authoring && linkPickerTask !== '__card__' && (
                <button
                  type="button"
                  onClick={() => { setLinkPickerTask('__card__'); setPickResourceId(''); setPickQty(1); }}
                  data-testid="card-link-resource-btn"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
                >
                  <Boxes className="h-3.5 w-3.5" /> Link Resource to card
                </button>
              )}
            </div>
            {cardLinkedResources.length > 0 ? (
              <div className="flex flex-wrap gap-1.5" data-testid="card-linked-resources">
                {cardLinkedResources.map((lr) => renderLinkedResource(
                  lr,
                  null,
                  isPerformed,
                  () => handleUnlinkResource('__card__', lr.resourceId)
                ))}
              </div>
            ) : (
              linkPickerTask !== '__card__' && <p className="text-[11px] text-gray-400">No resources linked to this card — link one for the whole card, or per task below.</p>
            )}
            {authoring && linkPickerTask === '__card__' && (
              <div className="flex flex-wrap items-center gap-2 bg-indigo-50/60 border border-indigo-100 rounded-md p-2 mt-1.5" data-testid="card-link-picker">
                {availableResources.length === 0 ? (
                  <span className="text-[11px] text-gray-500">Add resources first (Resources panel), then link them here.</span>
                ) : (
                  <>
                    <select
                      value={pickResourceId}
                      onChange={(e) => setPickResourceId(e.target.value)}
                      data-testid="card-resource-select"
                      className="text-xs border border-gray-300 rounded-md bg-white px-2 py-1.5 flex-1 min-w-[140px]"
                    >
                      <option value="">Select a resource…</option>
                      {availableResources.map((r) => (
                        <option key={r.id} value={r.id} disabled={liveAvail(r) <= 0}>
                          {availLabel(r)}
                        </option>
                      ))}
                    </select>
                    <Input
                      type="number"
                      min="1"
                      value={pickQty}
                      onChange={(e) => setPickQty(e.target.value)}
                      className="h-8 w-16 text-xs no-number-spinner"
                      title="Quantity"
                      data-testid="card-resource-qty"
                    />
                    <Button type="button" size="sm" className="h-8 text-xs" onClick={() => handleLinkResource('__card__')} data-testid="card-resource-add">Add</Button>
                  </>
                )}
                <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setLinkPickerTask(null); setPickResourceId(''); setPickQty(1); }}>Cancel</Button>
              </div>
            )}
          </div>

          {/* Tasks Checklist */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Tasks</Label>
              {authoring && (
                <span className="text-xs text-gray-500">
                  {formData.tasks.length} task{formData.tasks.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            
            {/* Add New Task (only for manual creation) */}
            {authoring && (
              <div className="mb-3">
                {isImmediateTask ? (
                  <div className="space-y-3 bg-slate-50 border border-slate-200/60 p-3.5 rounded-xl">
                    <div className="flex flex-col md:flex-row gap-3">
                      <div className="flex-1">
                        <Label htmlFor="taskDesc" className="text-xs text-slate-500 mb-1.5 block font-medium">Task Description</Label>
                        <Input
                          id="taskDesc"
                          placeholder="Enter immediate task description..."
                          value={newTaskText}
                          onChange={(e) => setNewTaskText(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddTask();
                            }
                          }}
                          className="bg-white border-slate-200 shadow-sm"
                        />
                      </div>
                      <div className="w-full md:w-32">
                        <Label htmlFor="taskTimer" className="text-xs text-slate-500 mb-1.5 block font-medium">Timer (minutes)</Label>
                        <Input
                          id="taskTimer"
                          type="number"
                          min="1"
                          value={newTaskTimer}
                          onChange={(e) => setNewTaskTimer(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                              e.preventDefault();
                            }
                          }}
                          className="bg-white border-slate-200 shadow-sm no-number-spinner"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end pt-1">
                      <Button
                        type="button"
                        onClick={handleAddTask}
                        variant="default"
                        size="sm"
                        className="h-8 text-xs font-semibold px-4"
                      >
                        + Add Immediate Task
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter task description..."
                      value={newTaskText}
                      onChange={(e) => setNewTaskText(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTask();
                        }
                      }}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={handleAddTask}
                      variant="outline"
                      size="sm"
                    >
                      + Add Task
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {formData.tasks && formData.tasks.length > 0 ? (
              <div className="mt-2 space-y-3 max-h-80 overflow-y-auto">
                {formData.tasks
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((task, index) => (
                    <div key={task.taskId} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="flex items-start gap-3">
                        {/* Task completion checkbox */}
                        {(() => {
                          const computed = isImmediateModeCard && isEditMode
                            ? computeTasksWithState(formData.tasks, editingItem?.meta?.notifiedAt || editingItem?.timestamp, currentTime)
                            : [];
                          const computedTask = computed.find(t => t.taskId === task.taskId);
                          const isFailed = computedTask ? (computedTask.state === 'failed' || computedTask.failedSLA) : task.failedSLA;
                          
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                if (isFailed) return;
                                handleTaskToggle(task.taskId);
                              }}
                              disabled={isFailed}
                              className={`mt-1 flex-shrink-0 ${isFailed ? 'cursor-not-allowed opacity-60' : ''}`}
                            >
                              {task.isCompleted ? (
                                <CheckCircle2 className="h-5 w-5 text-green-600" />
                              ) : isFailed ? (
                                <XCircle className="h-5 w-5 text-red-500" />
                              ) : (
                                <XCircle className="h-5 w-5 text-gray-400" />
                              )}
                            </button>
                          );
                        })()}
                        
                        {/* Task content */}
                        <div className="flex-1">
                          {/* Task text - editable for manual creation */}
                          {authoring ? (
                            <div className="space-y-2">
                              <div className="flex gap-2 items-center">
                                <Input
                                  value={task.taskText}
                                  onChange={(e) => handleUpdateTaskText(task.taskId, e.target.value)}
                                  className={`font-medium ${task.isCompleted ? 'line-through text-gray-500' : 'text-gray-900'} flex-1`}
                                  placeholder="Task description"
                                />
                                {!isImmediateTask && (
                                  <div className="w-28 flex items-center gap-1 flex-shrink-0">
                                    <Input
                                      type="number"
                                      min="0"
                                      value={task.sla ?? ''}
                                      onChange={(e) => handleUpdateTaskSla(task.taskId, e.target.value)}
                                      onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
                                      className="h-9 text-xs no-number-spinner"
                                      title="Task SLA (minutes)"
                                      placeholder="SLA"
                                      data-testid={`task-sla-${task.taskId}`}
                                    />
                                    <span className="text-xs text-slate-500">min</span>
                                  </div>
                                )}
                                {isImmediateTask && (
                                  <div className="w-24 flex items-center gap-1 flex-shrink-0">
                                    <Input
                                      type="number"
                                      min="1"
                                      value={task.timerMinutes || 5}
                                      onChange={(e) => handleUpdateTaskTimer(task.taskId, e.target.value)}
                                      className="h-9 text-xs no-number-spinner"
                                      title="Task timer (minutes)"
                                    />
                                    <span className="text-xs text-slate-500">mins</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div className={`font-medium ${task.isCompleted ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                {task.taskText}
                              </div>
                              {task.timerMinutes && (
                                <>
                                  {task.isCompleted ? (
                                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-green-50 text-green-700 border border-green-200/50 shadow-sm">
                                      ⏱️ {task.timerMinutes} mins timer (Completed)
                                    </div>
                                  ) : (() => {
                                    const activeInfo = getActiveTaskInfo();
                                    if (activeInfo && activeInfo.taskId === task.taskId) {
                                      const isOverdue = activeInfo.isOverdue;
                                      const displayTime = formatCountdown(activeInfo.remainingMs);
                                      return (
                                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold border shadow-sm ${
                                          isOverdue 
                                            ? 'bg-rose-50 text-rose-700 border-rose-200 animate-pulse' 
                                            : 'bg-amber-50 text-amber-700 border-amber-200'
                                        }`}>
                                          <span className={`h-2 w-2 rounded-full ${isOverdue ? 'bg-rose-600' : 'bg-amber-500'} animate-ping`} />
                                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          {isOverdue ? `Overdue: ${displayTime}` : `Active Timer: ${displayTime}`}
                                        </div>
                                      );
                                    }
                                    return (
                                      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-50 text-slate-500 border border-slate-200/40 shadow-sm">
                                        ⏱️ {task.timerMinutes} mins timer (queued)
                                      </div>
                                    );
                                  })()}
                                </>
                              )}
                            </div>
                          )}
                          
                          {/* Completion info */}
                          {task.isCompleted && task.completedAt && (
                            <div className="text-xs text-gray-500 mt-1">
                              Completed by {task.completedBy} at {new Date(task.completedAt).toLocaleString()}
                            </div>
                          )}

                          {/* Linked resources */}
                          {(task.linkedResources && task.linkedResources.length > 0) && (
                            <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`task-linked-resources-${task.taskId}`}>
                              {task.linkedResources.map((lr) => renderLinkedResource(
                                lr,
                                task.taskId,
                                task.isCompleted,
                                () => handleUnlinkResource(task.taskId, lr.resourceId)
                              ))}
                            </div>
                          )}

                          {/* Link Resource control (authoring only) */}
                          {authoring && (
                            <div className="mt-2">
                              {linkPickerTask === task.taskId ? (
                                <div className="flex flex-wrap items-center gap-2 bg-indigo-50/60 border border-indigo-100 rounded-md p-2" data-testid={`task-link-picker-${task.taskId}`}>
                                  {availableResources.length === 0 ? (
                                    <span className="text-[11px] text-gray-500">Add resources first (Resources panel), then link them here.</span>
                                  ) : (
                                    <>
                                      <select
                                        value={pickResourceId}
                                        onChange={(e) => setPickResourceId(e.target.value)}
                                        data-testid={`task-resource-select-${task.taskId}`}
                                        className="text-xs border border-gray-300 rounded-md bg-white px-2 py-1.5 flex-1 min-w-[140px]"
                                      >
                                        <option value="">Select a resource…</option>
                                        {availableResources.map((r) => (
                                          <option key={r.id} value={r.id} disabled={liveAvail(r) <= 0}>
                                            {availLabel(r)}
                                          </option>
                                        ))}
                                      </select>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={pickQty}
                                        onChange={(e) => setPickQty(e.target.value)}
                                        className="h-8 w-16 text-xs no-number-spinner"
                                        title="Quantity"
                                        data-testid={`task-resource-qty-${task.taskId}`}
                                      />
                                      <Button type="button" size="sm" className="h-8 text-xs" onClick={() => handleLinkResource(task.taskId)} data-testid={`task-resource-add-${task.taskId}`}>Add</Button>
                                    </>
                                  )}
                                  <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setLinkPickerTask(null); setPickResourceId(''); setPickQty(1); }}>Cancel</Button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => { setLinkPickerTask(task.taskId); setPickResourceId(''); setPickQty(1); }}
                                  data-testid={`task-link-resource-btn-${task.taskId}`}
                                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
                                >
                                  <Boxes className="h-3.5 w-3.5" /> Link Resource
                                </button>
                              )}
                            </div>
                          )}

                        </div>
                        
                        {/* Remove task button (only for manual creation) */}
                        {authoring && (
                          <div className="flex flex-col gap-1">
                            {/* Move up button */}
                            <button
                              type="button"
                              onClick={() => handleMoveTaskUp(index)}
                              disabled={index === 0}
                              className={`text-gray-400 hover:text-gray-600 ${index === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                              title="Move up"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            </button>
                            {/* Move down button */}
                            <button
                              type="button"
                              onClick={() => handleMoveTaskDown(index)}
                              disabled={index === formData.tasks.length - 1}
                              className={`text-gray-400 hover:text-gray-600 ${index === formData.tasks.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                              title="Move down"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {/* Remove button */}
                            <button
                              type="button"
                              onClick={() => handleRemoveTask(task.taskId)}
                              className="text-red-500 hover:text-red-700"
                              title="Remove task"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="mt-2 text-sm text-gray-500 italic p-4 bg-gray-50 rounded border border-gray-200">
                {!authoring ? (
                  "No tasks assigned to this action card."
                ) : (
                  "No tasks added yet. Click '+ Add Task' to add tasks."
                )}
              </div>
            )}
          </div>


          {/* Mark as Performed Button */}
          {!isPerformed && isEditMode && !isScenario && (
            <div>
              <Button
                type="button"
                onClick={handleMarkAsPerformed}
                disabled={performButtonLoading}
                className="w-full"
                variant="default"
              >
                {performButtonLoading ? 'Processing...' : 'Mark as Performed'}
              </Button>
            </div>
          )}

          {/* Performed Status */}
          {isPerformed && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-800 font-medium">
                <CheckCircle2 className="h-5 w-5" />
                Response Action Performed
              </div>
              {formData.performedAt && (
                <div className="mt-1 text-sm text-green-700">
                  Performed at: {new Date(formData.performedAt).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {/* Conditional follow-ups belong to scenario planning, not manual response-action creation. */}
          {!recipientCreate && isScenario ? (
            <ConditionalFollowupTrigger editingItem={editingItem} onAddConditional={onAddConditional} />
          ) : null}
        </div>

        {/* Footer */}
        <div className={`flex items-center gap-3 p-6 border-t border-gray-200 bg-gray-50 ${recipientCreate ? 'justify-between' : 'justify-end'}`}>
          {recipientCreate && (
            <span className="text-sm font-medium text-slate-600">
              {pickerSelections.length} recipient selection{pickerSelections.length === 1 ? '' : 's'}
            </span>
          )}
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={loading || (recipientCreate && pickerSelections.length === 0) || (isScenario && (planCardEdit || !realPlanReview) && !rolesValid(roles, incident?.is_scenario ? scenarioStaffingDefaults : null))}
              className={recipientCreate ? 'bg-violet-600 text-white hover:bg-violet-700' : undefined}
            >
              {loading
                ? (recipientCreate ? 'Creating...' : 'Saving...')
                : recipientCreate
                  ? `Create (${pickerSelections.length})`
                  : (isScenario && (planCardEdit || !realPlanReview))
                    ? 'Save roles'
                    : isEditMode
                      ? 'Save Changes'
                      : (realPlanReview ? 'Send' : 'Create Response Action')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseActionModal;
